import _ from 'lodash'
import dayjs from 'dayjs'
import { memo, useContext, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Button, Stack, Tab, Tabs } from '@mui/material'

import CheckboxInput from '@/components/ui/inputs/CheckboxInput'

import { ConsultationStatus, ConsultationType } from '@/core/models/consultation.model'

import useInterval from '@/hooks/useInterval'
import { useIsSA } from '@/hooks/useIsSA'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAppSettings } from '@/redux/reducers/app.reducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { createConsultation, selectConsultationCreateRequestTracker, selectNewlyCreatedConsultationId } from '@/redux/reducers/consultation.reducer'

import { isStaff } from '@/utils/type-guards'

import { CreateConsultationStepperContext } from '../CreateConsultationStepper'
import CreateConsultationStepLayout from '../components/CreateConsultationStepLayout'
import SelectStaffAvailabilityStep from './SelectStaffAvailabilityStep'
import SelectStartDateStep from './SelectStartDateStep'
import SelectWaitingRoomStep from './SelectWaitingRoomStep'
import SelectNetworkStep from './SelectNetworkStep'

const MemoizedCreateConsultationStepLayout = memo(CreateConsultationStepLayout)
const MemoizedSelectNetworkStep = memo(SelectNetworkStep)
const MemoizedSelectStaffAvailabilityStep = memo(SelectStaffAvailabilityStep)
const MemoizedSelectWaitingroomStep = memo(SelectWaitingRoomStep)

export default function SelectAppointmentStep() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const { initialValues, setValue, values } = useContext(CreateConsultationStepperContext)

  const [currentDateTime, setCurrentDateTime] = useState(dayjs())
  const [selectAppointmentStepTabValue, setSelectAppointmentStepTabValue] = useState(0)

  useInterval(() => {
    setCurrentDateTime(dayjs())
  }, 1000)

  const authUser = useAppSelector(selectAuthUser)
  const appSettings = useAppSelector(selectAppSettings)
  const newlyCreatedConsultationId = useAppSelector(selectNewlyCreatedConsultationId)

  const isDoctor = isStaff(authUser) && authUser?.status === 'MEDECIN'
  const isPds = isStaff(authUser) && authUser?.status === 'PDS'

  // SEHA (en-SA) uses the waiting room as its primary intake flow; other locales can opt in
  const waitingRoomFirst = useIsSA() || appSettings.preferWaitingRoom

  const orderedTabs = _.compact([
    isDoctor && {
      id: 'pds-availability',
      label: t('page.createConsultation.step.pdsAvailabilitySelection.label'),
      content: <MemoizedSelectStaffAvailabilityStep />,
    },
    isDoctor &&
      !initialValues.startDate && {
        id: 'select-start-date',
        label: t('page.createConsultation.step.startDateSelection.label', { context: 'withoutPds' }),
        content: <SelectStartDateStep currentDateTime={currentDateTime} />,
      },
    isPds && {
      id: 'doctor-availability',
      label: t('page.createConsultation.step.doctorAvailabilitySelection.label'),
      content: <MemoizedSelectStaffAvailabilityStep />,
    },
    isPds &&
      !initialValues.startDate && {
        id: 'waiting-room',
        label: t('page.createConsultation.step.waitingRoomSelection.label'),
        content: <MemoizedSelectWaitingroomStep />,
      },
    { id: 'network', label: t('network'), content: <MemoizedSelectNetworkStep /> },
  ])

  const waitingRoomTab = waitingRoomFirst ? orderedTabs.find((tab) => tab.id === 'waiting-room') : undefined
  const tabs = waitingRoomTab ? [waitingRoomTab, ...orderedTabs.filter((tab) => tab.id !== 'waiting-room')] : orderedTabs

  const selectedTab = tabs[selectAppointmentStepTabValue]
  const nextButtonDisabled =
    ((selectedTab.id === 'pds-availability' || selectedTab.id === 'doctor-availability') &&
      ((_.isNil(values.medecin) && isPds) ||
        (_.isNil(values.pds) && isDoctor) ||
        _.isNil(values.startDate) ||
        dayjs(values.startDate).isBefore(currentDateTime, 'minutes'))) ||
    (selectedTab.id === 'waiting-room' && ((isPds && values.waitingRoom === undefined) || (isDoctor && _.isNil(values.startDate)))) ||
    (selectedTab.id === 'network' && _.isNil(values.network))

  const startLocalConsultationRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.localConsultation.create.success'), { variant: 'success' })
      if (newlyCreatedConsultationId) {
        navigate(`/consultations/${newlyCreatedConsultationId}/local?${searchParams.toString()}`)
      }
    },
    onError: () => enqueueSnackbar(t('notification.consultation.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectConsultationCreateRequestTracker,
  })

  const handleChangeSelectAppointmentStepTabs = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectAppointmentStepTabValue(newValue)
    setValue('startDate', undefined)
  }

  const handleStartLocalConsultation = () => {
    if (values.patient) {
      startLocalConsultationRequestHandler.dispatch(
        createConsultation({
          consultation: {
            patient: values.patient,
            type: ConsultationType.Local,
            status: ConsultationStatus.Active,
            startDate: new Date().toISOString(),
          },
        })
      )
    }
  }

  const handleSelectMeetingOnly = () => {
    setValue('meetingOnly', !values.meetingOnly)
  }

  return (
    <MemoizedCreateConsultationStepLayout nextButtonDisabled={nextButtonDisabled}>
      <Stack flex={1} maxWidth="md">
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between">
          <Tabs value={selectAppointmentStepTabValue} onChange={handleChangeSelectAppointmentStepTabs} variant="scrollable" scrollButtons="auto">
            {tabs.map((tab) => (
              <Tab key={tab.id} label={tab.label} sx={{ fontWeight: 700 }} />
            ))}
          </Tabs>
          {!initialValues.medecin && !initialValues.startDate && (
            <Button onClick={handleStartLocalConsultation} id="start-local" variant="outlined" sx={{ mt: { xs: 2, sm: 0 } }}>
              {t('page.createConsultation.step.appointmentSelection.startLocalConsultationButton.label', {
                context: isDoctor ? 'doctor' : isPds ? 'pds' : undefined,
              })}
            </Button>
          )}
        </Stack>
        <Stack spacing={1} pt={2} pb={4}>
          <CheckboxInput
            checked={!values.meetingOnly}
            label={t('page.createConsultation.step.appointmentSelection.field.meetingOnly.label')}
            onChange={handleSelectMeetingOnly}
          />
          {tabs.map((tab, tabIndex) => (selectAppointmentStepTabValue === tabIndex ? tab.content : null))}
        </Stack>
      </Stack>
    </MemoizedCreateConsultationStepLayout>
  )
}
